import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';

/** Modular date picker
 *
 * This component provides a date picker UI with an associated reactive
 * form group. The form group provides 'day', 'month', and 'year'
 * fields. It is currently tailored to date of birth selection, but it can be
 * generalized later.
 *
 * To attach the form group to a parent component's form group, do the
 * following in the parent component:
 *
 * 1. In the declarations, inject this component as a view child:
 * @ViewChild(DatePickerComponent) datePickerComponent: DatePickerComponent;
 *
 * 2. Add the following to ngAfterViewInit:
 * this.form.addControl('date', this.datePickerComponent.formGroup);
 * this.datePickerComponent.formGroup.setParent(this.form);
 *
 * Special thanks to Dave M. Bush:
 * https://davembush.github.io/attaching-an-angular-child-component-s-form-to-a-parent/
 * */
@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DatePickerComponent
        }
    ],
    standalone: false
})
export class DatePickerComponent implements OnInit, OnDestroy, ControlValueAccessor {

  readonly allowedYears: number[];
  dayField = new FormControl(undefined, []);
  monthField = new FormControl(undefined, []);
  yearField = new FormControl(undefined, []);
  formGroup: FormGroup = new FormGroup({});
  readonly daysInCurrentMonth = new BehaviorSubject<any[]>(Array(31));
  date: string = ""

  @Input() dateParts: Set<('day' | 'month' | 'year')> = new Set(['day', 'month', 'year'])
  @Input() htmlIdPrefix = '';
  @Input() tooltipText = '';

  constructor(private readonly changeDetector: ChangeDetectorRef) {
    this.allowedYears = this.getBirthYearsForPeopleAgedBetween(18, 125);
  }

  onTouched: Function = () => {};
  onChangeSubs: Subscription[] = [];

  ngOnDestroy() {
    for (let sub of this.onChangeSubs) {
      sub.unsubscribe()
    }
  }

  ngOnInit() {
    if (this.dateParts.has('day')) {
      this.dayField = new FormControl(null, Validators.required)
    }
    if (this.dateParts.has('month')) {
      this.monthField = new FormControl(null, Validators.required)
    }
    if (this.dateParts.has('year')) {
      this.yearField = new FormControl(null, Validators.required)
    }
    this.formGroup = new FormGroup({
      day: this.dayField,
      month: this.monthField,
      year: this.yearField,
    })
    this.formGroup.valueChanges.pipe(
      map(v => Array(daysInMonth(v.month, v.year))),
      tap(v => this.handleDayOfMonthOverflow(v)),
    ).subscribe(this.daysInCurrentMonth);
  }
  
  registerOnChange(onChange: any) {
    const sub = this.formGroup.valueChanges.subscribe(onChange);
    this.onChangeSubs.push(sub);
  }

  registerOnTouched(onTouched: Function) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.formGroup.disable();
    }
    else {
      this.formGroup.enable();
    }
  }

  writeValue(value: any) {
    if (value) {
      this.formGroup.setValue(value, {emitEvent: false});
    }
  }

  private getBirthYearsForPeopleAgedBetween(youngest: number, oldest: number): number[] {
    const currentYear = this.getCurrentYearInKiritimati();
    const years: number[] = [];
    for (let i = currentYear - youngest; i > currentYear - oldest; i--) {
      years.push(i);
    }
    return years;
  }

  /** Get the current year in the most advanced time zone on Earth
   *
   * We need to show an 18 year old their birth year no matter where
   * their home jurisdiction might be. Kiritimati, in UTC+14, is the
   * first place on Earth to enter the new year.
   *
   * For example, A Kiritimatian who was born on new years' day in 2004
   * and lives in Hawaii (UTC-12) needs to be able to vote on 31
   * December 2021 Hawaii time.
   *
   * Therefore, the determination of which birth years to show is based
   * on the current year in UTC+14. This guarantees that all 18 year
   * olds on earth see their birth year regardless of any time zone
   * difference.
   **/
  getCurrentYearInKiritimati(): number {
    const now = new Date();
    return new Date(now.getTime() + 1000 * 3600 * 14).getUTCFullYear();
  }

  anyFieldsMissing(): boolean {
    return ![...this.dateParts].every(f =>
      !this.formGroup.get(f)?.errors?.required
    )
  }

  private handleDayOfMonthOverflow(daysInNewMonth: number[]): void {
    const currentDay = this.dayField.value;
    if (currentDay !== null && currentDay !== undefined && daysInNewMonth.includes(currentDay)) {
      this.dayField.setValue(null);
      this.dayField.updateValueAndValidity();
      this.changeDetector.detectChanges();
    }
  }

  onTooltipClick(event: any) {
    event.preventDefault();
  }
}

function daysInMonth(month: string | null, year: string | null): number {
  if (month === null) {
    return 31;
  } else if (month === '2' && year === null) {
    return 29;
  } else {
    return new Date(Number(year || '2001'), Number(month), 0).getDate();
  }
}
